import {IWayPointCreationSingleVoyage} from "@containers/singleVoyage/types/point";

/** Waypoints are saved with at least this radius, see track-api CloseStopsCheck. */
export const MIN_STORED_RADIUS_SINGLE_VOYAGE = 30
// Vehicles stop up to this far from a stop's centre, see track-api CloseStopsCheck.STAND_MARGIN
export const STAND_MARGIN_SINGLE_VOYAGE = 20
const SAME_PLACE_METERS = 1
const EARTH_RADIUS_METERS = 6_371_000

export interface ICloseStopsConflictSingleVoyage {
	first: IWayPointCreationSingleVoyage
	second: IWayPointCreationSingleVoyage
	firstNumber: number
	secondNumber: number
	/** Rounded down, so the radius suggested from it always passes the check. */
	distance: number
	radius: number
	/** Largest radius at which a vehicle standing at either stop stays out of the other zone. */
	maxRadius: number
}

const isPointStop = (point: IWayPointCreationSingleVoyage): boolean => {
	return point.type === 'POINT_RADIUS' || point.type === 'RFID_POINT'
}

// A stop from a stop template takes its radius from there, so the voyage editor cannot change it
export const hasOwnRadiusSingleVoyage = (point: IWayPointCreationSingleVoyage): boolean => {
	return !point.waypointTemplateId && (!point.status || point.status === 'PLANNED')
}

export const storedRadiusSingleVoyage = (radius: number | null | undefined): number => {
	return Math.max(radius ?? 0, MIN_STORED_RADIUS_SINGLE_VOYAGE)
}

const distanceMeters = (
	[lng1, lat1]: [number, number],
	[lng2, lat2]: [number, number],
): number => {
	const toRad = (deg: number) => deg * Math.PI / 180
	const dLat = toRad(lat2 - lat1)
	const dLng = toRad(lng2 - lng1)
	const h = Math.sin(dLat / 2) ** 2
		+ Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
	return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(h))
}

/**
 * Point stops where a vehicle standing at one is in the zone of the other, by the same rule
 * track-api enforces on save: telematic checks every zone on its own, so both visits would open.
 * Numbers are 1-based positions among all waypoints, as the list and the map show them.
 */
export const findCloseStopsSingleVoyage = (
	points: IWayPointCreationSingleVoyage[] | null | undefined,
): ICloseStopsConflictSingleVoyage[] => {
	const conflicts: ICloseStopsConflictSingleVoyage[] = []
	if (!points?.length) {
		return conflicts
	}

	points.forEach((first, i) => {
		if (!isPointStop(first) || !first.geom?.coordinates) return
		for (let j = i + 1; j < points.length; j++) {
			const second = points[j]
			if (!isPointStop(second) || !second.geom?.coordinates) continue

			const distance = distanceMeters(first.geom.coordinates, second.geom.coordinates)
			// TODO: decide how a stop listed twice (e.g. a round trip) should be visited; until then it is allowed
			const sameStop = (first.waypointTemplateId != null && first.waypointTemplateId === second.waypointTemplateId)
				|| distance < SAME_PLACE_METERS
			if (sameStop) continue

			const radius = Math.max(storedRadiusSingleVoyage(first.radius), storedRadiusSingleVoyage(second.radius))
			if (distance < radius + STAND_MARGIN_SINGLE_VOYAGE) {
				conflicts.push({
					first,
					second,
					firstNumber: i + 1,
					secondNumber: j + 1,
					distance: Math.floor(distance),
					radius,
					maxRadius: Math.floor(distance) - STAND_MARGIN_SINGLE_VOYAGE,
				})
			}
		}
	})
	return conflicts
}
